/* eslint-disable no-param-reassign */

// Third-party
import React, { Component } from 'react';
import deepmerge from 'deepmerge';
import { isEqual } from 'lodash';
import moment from 'moment';
import Highcharts, {
  AxisLabelsFormatterContextObject,
  FormatterCallbackFunction,
  SeriesLineOptions,
  XAxisLabelsOptions,
  XAxisOptions,
  YAxisLabelsOptions,
  YAxisOptions,
} from 'highcharts/highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';

// Namespace
import * as ProposalNS from '../../Proposal';

// Components
import { withErrorBoundary } from '../../../../views/components/errors';

// Utils
import { FormattedMessage, formatNumber, LOCALE } from '../../../../utils/i18n';
import { capitalizeFirst } from '../../../../utils/strings';
import { convertConfigOptionsToHighcharts, LABEL_FORMATTERS } from '../../../../utils/charts';
import LineSeriesData from '@/types/line-series-data';
import Locale from '@/types/locale';
import AccessibilityDescription from '../misc/AccessibilityDescription';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LocalizedString from '@/types/localized-string';

export type LineChartOptions = Highcharts.Options;

type Props = {
  title?: string;
  dark?: boolean;
  data: LineSeriesData[];
  configOptions: Partial<ProposalNS.LineChartConfigOptions>;
  className?: string;
  locale?: Locale;
  accessibilityDescription?: LocalizedString;
  toolbarOption?: ProposalToolbarOption;
};

class ProposalLineChart extends Component<Props> {
  readonly chartMarker: Highcharts.PlotAreaMarkerOptions = {
    symbol: 'circle',
    radius: 0,
    states: {
      hover: {
        lineWidth: 3,
        radiusPlus: 5,
      },
      inactive: {
        opacity: 0,
      },
    },
  };

  readonly DEFAULT_CHART_OPTIONS: LineChartOptions = {
    series: [] as Highcharts.SeriesLineOptions[],
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    navigator: { enabled: false },
    chart: {
      height: 250,
      spacingLeft: 0,
      spacingRight: 0,
    },
    tooltip: {
      enabled: true,
      useHTML: false,
      pointFormat:
        '<span style="color:{point.color};">\u25CF</span>\u00A0<span data-private data-dd-privacy="mask">{series.name}</span>: <span style="color:#A4A7B7;font-weight: bold;">{point.y}</span><br/>',
      backgroundColor: '#000000',
      borderColor: '#000000',
      borderRadius: 6,
      split: true,
      shadow: false,
      style: {
        color: '#ffffff',
      },
    },
    title: {
      text: undefined,
    },
    xAxis: {
      crosshair: {
        className: 'x-crosshair',
        width: 0,
      },
      minTickInterval: 2735696401, // 1 month in milliseconds
      tickWidth: 0,
      minorTickWidth: 0,
      lineWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      labels: {
        formatter() {
          return capitalizeFirst(moment(new Date(this.value)).format('MMM YYYY'));
        },
      },
    },
    yAxis: {
      title: {
        text: undefined,
      },
      endOnTick: true,
      gridLineWidth: 1,
      labels: {
        formatter() {
          return formatNumber(this.value, 1);
        },
      },
    },
    plotOptions: {
      area: {
        marker: this.chartMarker,
        threshold: null as any, // Typescript issue with our version of highcharts. Fixed in v9.0.0.
      },
      line: this.chartMarker,
      series: {
        animation: true,
      },
    },
  };

  static defaultProps: Props = {
    configOptions: {},
    data: [],
  };

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  formatLineSeriesData = (series: LineSeriesData[]): SeriesLineOptions[] =>
    series.map(
      s =>
        ({
          data: s.data,
          color: s.color,
          name: s.name,
          dashStyle: s.dashStyle,
          lineWidth: s.lineWidth,
          tooltip: s.tooltip,
          type: s.fillColor ? 'area' : 'line',
          fillColor: s.fillColor,
          marker: {
            symbol: 'circle',
            radius: 0.2,
          },
          zIndex: s.zIndex,
          zoneAxis: s.zoneAxis,
          zones: s.zones,
        }) as SeriesLineOptions,
    );

  getHighChartOptions = (data: LineSeriesData[]): LineChartOptions => {
    const { configOptions, locale } = this.props;

    const config = convertConfigOptionsToHighcharts('line', configOptions, locale);
    const chartOptions = deepmerge(this.DEFAULT_CHART_OPTIONS, config);

    this.applyFormatter(chartOptions);

    return { ...chartOptions, series: this.formatLineSeriesData(data) } as LineChartOptions;
  };

  applyFormatter(options: Highcharts.Options) {
    const { locale, configOptions } = this.props;

    if (options.yAxis === undefined) {
      options.yAxis = {} as YAxisOptions;
    }

    const localYAxis: YAxisOptions = options.yAxis as YAxisOptions;
    if (localYAxis.labels === undefined) {
      localYAxis.labels = {} as YAxisLabelsOptions;
    }

    switch (configOptions.yAxis?.labels?.formatterType) {
      case ProposalNS.ChartLabelFormatterType.Dollars: {
        localYAxis.labels.formatter = LABEL_FORMATTERS.dollars[locale || LOCALE.EN_CA].formatter as FormatterCallbackFunction<AxisLabelsFormatterContextObject>;
        break;
      }
      case ProposalNS.ChartLabelFormatterType.DollarsNoSuffix: {
        localYAxis.labels.formatter = LABEL_FORMATTERS.dollarsNoSuffix[locale || LOCALE.EN_CA]
          .formatter as FormatterCallbackFunction<AxisLabelsFormatterContextObject>;
        break;
      }
      case ProposalNS.ChartLabelFormatterType.Decimal: {
        const { decimals } = configOptions.yAxis.labels;
        localYAxis.labels.formatter = LABEL_FORMATTERS.decimal[locale || LOCALE.EN_CA].getFormatter(
          decimals,
        ) as FormatterCallbackFunction<AxisLabelsFormatterContextObject>;
        break;
      }
    }

    if (options.xAxis === undefined) {
      options.xAxis = {} as XAxisOptions;
    }

    const localXAxis: XAxisOptions = options.xAxis as XAxisOptions;
    if (localXAxis.labels === undefined) {
      localXAxis.labels = {} as XAxisLabelsOptions;
    }

    if (configOptions !== undefined && configOptions.xAxis && configOptions.xAxis.labels) {
      // Currently only BMO GIF uses a specific formatter (to include date of birth)
      if (configOptions.xAxis.labels.formatter) {
        localXAxis.labels.formatter = configOptions.xAxis.labels.formatter;
      } else {
        // DATE
        if (configOptions.xAxis.labels.formatterType === ProposalNS.ChartLabelFormatterType.Date) {
          localXAxis.labels.formatter = LABEL_FORMATTERS.date[locale || LOCALE.EN_CA].formatter as FormatterCallbackFunction<AxisLabelsFormatterContextObject>;
        }
      }
    }
  }

  getDataItems(itemsData?: LineSeriesData[]) {
    return itemsData?.map(item => item.name || '') || [];
  }

  render() {
    const { dark, className, locale, data, accessibilityDescription, toolbarOption } = this.props;
    const highchartsOptions = this.getHighChartOptions(data);

    const chartHasData = highchartsOptions.series && highchartsOptions.series.some(serie => !!serie.data && serie.data.length > 0);
    if (highchartsOptions.chart !== undefined) {
      highchartsOptions.chart.animation = false;
    }

    return (
      <div className={classNames('proposal-line-chart', className, { dark })}>
        <div className="graph-container">
          {accessibilityDescription && (
            <AccessibilityDescription description={accessibilityDescription} items={this.getDataItems(data)} locale={locale} toolbarOption={toolbarOption} />
          )}

          {chartHasData && <HighchartsReact highcharts={Highcharts} options={highchartsOptions} />}

          {!chartHasData && (
            <div className="empty-chart flex-fill">
              <FormattedMessage id="Charts.EmptyPeriod" locale={locale} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export type LineChartProps = Props;

export default withErrorBoundary(ProposalLineChart);
